import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import ConfirmDelete from "@/components/ConfirmDelete";
import { fleet as fleetApi, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { UsersThree, Link as LinkIcon, Copy, ShareNetwork, PencilSimple, Check, X, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Fleet() {
  const [data, setData] = useState(null);
  const [lastLink, setLastLink] = useState(null);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const load = () => fleetApi.get().then(setData).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <AppShell><div className="text-muted-foreground" data-testid="fleet-loading">Loading your fleet…</div></AppShell>;
  const owner = data.my_role === "owner";
  const full = data.seats_used >= data.seat_limit;

  const invite = async (email) => {
    try {
      const r = await fleetApi.invite(email || undefined);
      setLastLink(r.url); load();
      if (!email) toast.success("Invite link ready");
      else if (r.email_sent) toast.success(`Invite emailed to ${email}`);
      else toast.error(r.email_error || "Email didn't send. Copy the link instead.");
      return true;
    } catch (e) { toast.error(formatErr(e)); return false; }
  };

  return <AppShell>
    <div className="mb-6">
      <div className="eyebrow text-accent">{owner ? "You own this fleet" : "You drive for this fleet"}</div>
      <FleetName name={data.fleet?.name} editable={owner} onSaved={load}/>
    </div>

    <SeatMeter data={data}/>

    {owner && <section className="mt-6 bg-white border border-border/60 p-5" data-testid="invite-section">
      <h2 className="font-display font-bold text-xl">Invite a driver</h2>
      <p className="text-sm text-muted-foreground mt-1">Email the invite to your driver, or create a link and text it. Each invite works once and expires in 7 days. Every driver login counts as one truck on your plan.</p>
      {full
        ? <div className="mt-4 border-l-4 border-l-accent bg-accent/10 p-3 text-sm" data-testid="seats-full">
            {data.seat_limit === 0 ? "Your plan has ended. Choose a plan to invite drivers." : `All ${data.seat_limit} truck spot${data.seat_limit === 1 ? " is" : "s are"} in use. Upgrade your plan or remove a driver to invite someone new.`}{" "}
            <Link to="/app/billing" className="font-bold underline">See plans</Link>
          </div>
        : <InviteForm onInvite={invite}/>}
      {lastLink && <InviteLink url={lastLink}/>}
      {data.invites.length > 0 && <div className="mt-5">
        <div className="eyebrow text-muted-foreground mb-2">Unused links</div>
        <ul className="space-y-2">
          {data.invites.map(i => <li key={i.token} className="flex items-center justify-between gap-2 text-sm border border-border/60 px-3 py-2" data-testid={`invite-${i.token}`}>
            <span className="font-mono-tech text-xs truncate">{i.email ? <span data-testid={`invite-email-${i.token}`}>{i.email} · </span> : "…/join/" + i.token.slice(0, 8) + "… · "}expires {new Date(i.expires_at).toLocaleDateString()}</span>
            <button onClick={async () => { try { await fleetApi.revoke(i.token); load(); toast.success("Link cancelled"); } catch (e) { toast.error(formatErr(e)); } }}
              data-testid={`btn-revoke-${i.token}`} className="text-destructive text-xs font-bold uppercase shrink-0">Cancel link</button>
          </li>)}
        </ul>
      </div>}
    </section>}

    <section className="mt-6">
      <h2 className="font-display font-bold text-xl mb-3">People in this fleet</h2>
      <ul className="space-y-2">
        {data.members.map(m => <li key={m.user_id} data-testid={`member-${m.user_id}`} className="flex flex-wrap items-center gap-3 bg-white border border-border/60 p-4">
          <UsersThree size={24} weight="duotone" className="text-primary"/>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold truncate">{m.name}</div>
            <div className="text-xs text-muted-foreground truncate">{m.email}</div>
          </div>
          <span className={`eyebrow ${m.fleet_role === "owner" ? "text-accent" : "text-muted-foreground"}`}>{m.fleet_role === "owner" ? "Owner" : "Driver"}</span>
          {owner && m.fleet_role !== "owner" && <ConfirmDelete testid={`btn-remove-${m.user_id}`} label="Remove"
            title={`Remove ${m.name}?`} description="They lose access to this fleet's records right away and free up a truck spot. Records they added stay with the fleet."
            onConfirm={async () => { try { await fleetApi.remove(m.user_id); toast.success("Driver removed"); load(); } catch (e) { toast.error(formatErr(e)); } }}/>}
        </li>)}
      </ul>
    </section>

    {!owner && <div className="mt-8">
      <ConfirmDelete testid="btn-leave-fleet" label="Leave fleet" title={`Leave ${data.fleet?.name}?`}
        description="You'll lose access to this fleet's loads, vehicles and locations. Records you added stay with the fleet."
        onConfirm={async () => { try { await fleetApi.leave(); await refresh(); toast.success("You left the fleet"); nav("/app"); } catch (e) { toast.error(formatErr(e)); } }}/>
    </div>}
  </AppShell>;
}

function SeatMeter({ data }) {
  const pct = data.seat_limit ? Math.min(100, (data.seats_used / data.seat_limit) * 100) : 100;
  const label = data.billing_status === "trialing" ? "Free trial" : data.billing_status === "complimentary" ? "Complimentary" : data.plan?.name || "No plan";
  return <div className="bg-white border border-border/60 p-5" data-testid="seat-meter">
    <div className="flex items-end justify-between gap-2">
      <div>
        <div className="eyebrow text-muted-foreground">Trucks in use · {label}</div>
        <div className="font-display font-black text-3xl mt-1" data-testid="seat-count">{data.seat_limit ? `${data.seats_used} of ${data.seat_limit}` : `${data.seats_used} · plan ended`}</div>
      </div>
      {data.my_role === "owner" && <Link to="/app/billing" className="eyebrow text-accent whitespace-nowrap">Change plan →</Link>}
    </div>
    <div className="h-2 bg-secondary mt-3"><div className={`h-2 ${pct >= 100 ? "bg-destructive" : "bg-accent"}`} style={{ width: `${pct}%` }}/></div>
    {data.billing_status === "trialing" && <p className="text-xs text-muted-foreground mt-2">During the free trial a fleet can have up to {data.seat_limit} people. Your plan sets the limit after that.</p>}
    {data.seats_used > data.seat_limit && data.seat_limit > 0 && <p className="text-xs text-destructive mt-2" data-testid="over-limit">Your fleet has more drivers than your plan covers. Upgrade or remove drivers.</p>}
  </div>;
}

function InviteForm({ onInvite }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (withEmail) => {
    setBusy(true);
    const ok = await onInvite(withEmail ? email.trim() : null);
    if (ok && withEmail) setEmail("");
    setBusy(false);
  };
  return <div className="mt-4 space-y-3">
    <form onSubmit={(e) => { e.preventDefault(); if (email.trim()) run(true); }} className="flex flex-col sm:flex-row gap-2">
      <input type="email" required data-testid="in-invite-email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="driver@email.com" className="flex-1 min-w-0 border-2 border-border focus:border-accent px-3 py-2 outline-none"/>
      <button data-testid="btn-email-invite" disabled={busy} className="btn-amber disabled:opacity-50"><EnvelopeSimple size={18} weight="bold"/> {busy ? "Sending…" : "Email invite"}</button>
    </form>
    <button type="button" onClick={() => run(false)} disabled={busy} data-testid="btn-create-invite" className="btn-dark disabled:opacity-50"><LinkIcon size={18} weight="bold"/> Or just create a link</button>
  </div>;
}

function InviteLink({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy automatically. Press and hold the link to copy it."); }
  };
  const share = () => navigator.share({ title: "Join my fleet on HaulGuard", text: "Join my fleet on HaulGuard:", url }).catch(() => {});
  return <div className="mt-4 bg-secondary p-3 slide-up" data-testid="invite-link-box">
    <div className="font-mono-tech text-xs break-all" data-testid="invite-link">{url}</div>
    <div className="flex gap-2 mt-3">
      <button onClick={copy} data-testid="btn-copy-invite" className="btn-dark !py-2 !px-3 !text-xs">{copied ? <Check size={16}/> : <Copy size={16}/>} {copied ? "Copied" : "Copy link"}</button>
      {navigator.share && <button onClick={share} data-testid="btn-share-invite" className="btn-amber !py-2 !px-3 !text-xs"><ShareNetwork size={16}/> Text it</button>}
    </div>
  </div>;
}

function FleetName({ name, editable, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(name || "");
  const save = async () => {
    try { await fleetApi.rename(val); setEdit(false); onSaved(); toast.success("Fleet name saved"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (edit) return <div className="flex gap-2 mt-1">
    <input value={val} onChange={(e) => setVal(e.target.value)} data-testid="in-fleet-name" className="flex-1 min-w-0 border-2 border-accent px-3 py-2 font-display font-bold text-xl"/>
    <button onClick={save} data-testid="btn-save-fleet-name" className="btn-amber !px-3"><Check size={18}/></button>
    <button onClick={() => setEdit(false)} className="btn-dark !px-3" aria-label="cancel"><X size={18}/></button>
  </div>;
  return <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight flex items-center gap-3" data-testid="fleet-name">
    {name}
    {editable && <button onClick={() => { setVal(name || ""); setEdit(true); }} data-testid="btn-edit-fleet-name" aria-label="rename"><PencilSimple size={22}/></button>}
  </h1>;
}

export function JoinFleet() {
  const { token } = useParams();
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { fleetApi.info(token).then(setInfo).catch(() => setInfo({ valid: false, reason: "Couldn't check this invite. Try again." })); }, [token]);
  useEffect(() => { if (info?.valid) sessionStorage.setItem("hg-join", token); }, [info, token]);

  const join = async () => {
    setBusy(true);
    try { const r = await fleetApi.join(token); sessionStorage.removeItem("hg-join"); await refresh(); toast.success(`You joined ${r.fleet_name}`); nav("/app"); }
    catch (e) { toast.error(formatErr(e)); setBusy(false); }
  };
  const dismiss = () => { sessionStorage.removeItem("hg-join"); nav(user ? "/app" : "/"); };

  return <div className="min-h-screen grid-pattern flex items-center justify-center p-4">
    <div className="bg-white border border-border/60 p-8 max-w-md w-full corner-bracket relative" data-testid="join-card">
      <div className="eyebrow text-accent mb-2">Fleet invite</div>
      {!info || loading ? <p className="text-muted-foreground">Checking your invite…</p>
      : !info.valid ? <>
          <h1 className="font-display font-black text-2xl" data-testid="join-invalid">Invite not available</h1>
          <p className="text-muted-foreground mt-2">{info.reason}</p>
          <button onClick={dismiss} className="btn-dark mt-6">OK</button>
        </>
      : <>
          <h1 className="font-display font-black text-2xl" data-testid="join-title">Join {info.fleet_name}</h1>
          <p className="text-muted-foreground mt-2">{info.owner_name ? `${info.owner_name} invited you` : "You've been invited"} to drive with this fleet on HaulGuard. Everyone in the fleet shares loads, vehicles and saved locations.</p>
          {user ? <>
              <p className="text-xs text-muted-foreground mt-4">Signed in as <b>{user.email}</b>. {user.fleet_role === "driver" ? "You'll leave your current fleet; its records stay there." : "Any records you've already added move into this fleet."}</p>
              <div className="flex gap-2 mt-5">
                <button onClick={join} disabled={busy} data-testid="btn-join-fleet" className="btn-amber flex-1">{busy ? "Joining…" : "Join fleet"}</button>
                <button onClick={dismiss} data-testid="btn-join-decline" className="btn-dark">Not now</button>
              </div>
            </>
          : <div className="flex flex-col gap-2 mt-6">
              <Link to="/register" data-testid="btn-join-register" className="btn-amber">Create my driver account</Link>
              <Link to="/login" data-testid="btn-join-login" className="btn-dark">I already have an account</Link>
            </div>}
        </>}
    </div>
  </div>;
}
